import React, { useCallback, useMemo } from "react";
import { PaymentMethod } from "../../../../common/enums";
import { CheckDisplayOverride, type RedactedPaymentPref } from "../../common/types";
import { getAddressSummaryDisplay } from "../../common/utils";
import { BasePaymentPrefDisplay } from "../BasePaymentPrefDisplay";
import { ExistingPaymentPrefDetails } from "../ExistingPaymentPrefDetails";
import { InvalidInstructionsWarning } from "../InvalidInstructionsWarning";
import { PaymentPrefVisibleFields } from "../PaymentPrefDetails/types";
import { PositionListOfPaymentPref } from "../PositionList/PositionListOfPaymentPref";

export type ExistingPaymentPrefDisplayProps = {
  /** Existing payment preference data to display */
  paymentPref: RedactedPaymentPref;
  /** Whether to show the list of positions using this payment preference */
  displayPositions?: boolean;
  /** Whether to show the reveal/hide details button */
  displayRevealDetails?: boolean;
  /** Whether to show warning for invalid payment instructions */
  displayInvalidInstructionsWarning?: boolean;
  /** Whether details should be expanded by default */
  defaultExpanded?: boolean;
  /** Optional accessory content (string or component) displayed between title and reveal button */
  accessory?: React.ReactNode;
  /** Optional accessory content displayed at the far right of the header */
  rightAccessory?: React.ReactNode;
  /** Specify which fields to show. If omitted, all fields are shown by default. */
  visibleFields?: PaymentPrefVisibleFields;
  /** Whether to show the bank domicile field */
  showBankDomicile?: boolean;
  /** Whether the card should take up the full width */
  isFullWidth?: boolean;
  /** Override options for check payment display */
  checkDisplayOverride?: CheckDisplayOverride;
};

/**
 * Component for displaying existing payment preference information with configurable field visibility.
 *
 * This component wraps BasePaymentPrefDisplay to show existing payment preferences with additional
 * features like position lists and invalid instruction warnings. It supports displaying accessory
 * content and controlling which fields are visible.
 */
export const ExistingPaymentPrefDisplay: React.FC<ExistingPaymentPrefDisplayProps> = ({
  paymentPref,
  displayPositions,
  displayRevealDetails,
  displayInvalidInstructionsWarning,
  defaultExpanded,
  accessory,
  rightAccessory,
  visibleFields,
  showBankDomicile,
  isFullWidth,
  checkDisplayOverride,
}: ExistingPaymentPrefDisplayProps) => {
  const renderDetails = useCallback(
    () => (
      <ExistingPaymentPrefDetails
        paymentPrefId={paymentPref.id}
        visibleFields={visibleFields}
        showBankDomicile={showBankDomicile}
        checkDisplayOverride={checkDisplayOverride}
      />
    ),
    [paymentPref.id, visibleFields, showBankDomicile],
  );

  const titleOverride = useMemo(() => {
    // If we have a check display override, we want to use that as the title
    if (
      paymentPref.paymentMethod === PaymentMethod.Check &&
      checkDisplayOverride?.checkPayeeOverride &&
      checkDisplayOverride?.checkAddressOverride
    ) {
      const payeeName = checkDisplayOverride.checkPayeeOverride;
      const address = checkDisplayOverride.checkAddressOverride;
      const addressSummary = getAddressSummaryDisplay(address);
      return [payeeName, addressSummary].filter(Boolean).join("; ");
    }
    return undefined;
  }, []);

  return (
    <BasePaymentPrefDisplay
      paymentPref={paymentPref}
      displayRevealDetails={displayRevealDetails}
      renderDetails={renderDetails}
      defaultExpanded={defaultExpanded}
      accessory={accessory}
      rightAccessory={rightAccessory}
      isFullWidth={isFullWidth}
      titleOverride={titleOverride}
    >
      {displayPositions && <PositionListOfPaymentPref id={paymentPref.id} />}
      {displayInvalidInstructionsWarning && paymentPref.verificationStatus === "error" && (
        <InvalidInstructionsWarning
          paymentPref={paymentPref}
          displayNocCorrectionDetails={displayRevealDetails}
        />
      )}
    </BasePaymentPrefDisplay>
  );
};
